/* istanbul ignore file */
import type { ReactNode } from 'react';
import React from 'react';
import {
  Banner,
  FloatingBannerImage,
  IconAppStore,
  IconGooglePlay,
} from '@noths/polaris-client-ribbons-design-system';
import { getContentstackImagesForWidths, getPathnameFromUrl } from '@noths/polaris-client-utils';
import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import type { GetServerSideProps } from 'next';

import { Seo } from 'src/components/atoms/Seo/Seo';
import { getFeatureFlagsWithMatchers } from 'src/featureFlags/getFeatureFlagsWithMatchers';
import { fetchGlobalContent } from 'src/services/contentstack/fetchGlobalContent';
import * as styles from 'src/styles/DownloadTheApp.styles';
import { trackDownloadAppClick } from 'src/tracking/components/apps';

const { colors } = themeVariables;
const appStoreAppsFlyerUrl = 'https://notonthehighstreet.onelink.me/XbOV/af2df437';
const googlePlayAppsFlyerUrl = 'https://notonthehighstreet.onelink.me/XbOV/7f57a61e';

const appQRCode =
  'https://images.contentstack.io/v3/assets/blt63f2f7afd19b09c2/blt2c96899845f8f729/68222007f2fd7ab4910278c9/apps-qr-code.png';

interface IconListProps {
  children: ReactNode;
  start?: number;
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { req } = context;
  const featureFlags = (req && getFeatureFlagsWithMatchers(req)) || {};
  const pathname = getPathnameFromUrl(req.url ?? '');
  const [siteChromeContent] = await fetchGlobalContent({
    featureFlags,
    urlPath: pathname,
  });

  return {
    props: {
      siteChromeContent,
    },
  };
};

const BannerImage = ({ imgUrl, offset }: { imgUrl: string; offset?: 'top' | 'bottom' }) => (
  <FloatingBannerImage
    imageAlt="Not On The Highstreet mobile app"
    imageHeight={216}
    imageWidth={360}
    images={getContentstackImagesForWidths(imgUrl, [360, 500, 700, 900, 1132], {
      aspectRatio: '16:9',
    })}
    offset={offset}
  />
);

const HeroBanner = () => (
  <Banner
    backgroundColor={`color-mix(in srgb, var(${colors.surface.action}) 10%, var(${colors.surface.default}))`}
  >
    <div css={styles.container}>
      <div css={[styles.imageWrapper, styles.heroBannerImageWrapper]}>
        <BannerImage imgUrl="https://images.contentstack.io/v3/assets/blt63f2f7afd19b09c2/bltd4fbf33c14e42308/69dfe1a49c717ba19e45e27a/Header.png" />
      </div>
      <div css={styles.content}>
        <div css={styles.darkTextContent}>
          <h2 css={styles.heading}>&apos;Appy&apos; shopping</h2>
          <h3 css={styles.subHeading}>
            Find small brands you’ll love, even faster, with our app. Download now to…
          </h3>
          <div css={styles.copy}>
            <a href={appStoreAppsFlyerUrl} onClick={trackDownloadAppClick} title="App Store">
              <IconAppStore height="40" width="121" />
            </a>
            <a href={googlePlayAppsFlyerUrl} onClick={trackDownloadAppClick} title="Google Play">
              <IconGooglePlay height="40" width="135" />
            </a>
          </div>
        </div>
      </div>
    </div>
  </Banner>
);

const IconList = ({ children, start }: IconListProps) => (
  <ol
    css={styles.iconsBulletList}
    start={start}
    style={start != null ? ({ '--ol-start': start - 1 } as React.CSSProperties) : undefined}
  >
    {children}
  </ol>
);

const IconItem = ({ text }: { text: string }) => (
  <li css={styles.iconsBulletItem}>
    <p css={styles.iconsBulletItemText}>{text}</p>
  </li>
);

const title = 'Download the Not On The High Street App';
const copy =
  "Download the free Not On The High Street App to share, save and receive all of our latest offers and updates. Plus, it's got a five-star rating, hurrah!";

const DownloadTheAppsPage = () => (
  <>
    <Seo description={copy} title={title} />
    <div css={styles.wrapper}>
      <HeroBanner />
    </div>

    <div css={styles.wrapper}>
      <div css={[styles.container, styles.contentReverse, styles.narrowContainer]}>
        <div css={styles.imageWrapper}>
          <BannerImage
            imgUrl="https://images.contentstack.io/v3/assets/blt63f2f7afd19b09c2/bltf86974332c9d0ca7/69dfe1a4d9c60a51aef94ac0/Header_2.png"
            offset="bottom"
          />
        </div>
        <div css={[styles.content, styles.indentedContent]}>
          <IconList>
            <IconItem text="Order update notifications directly to your phone" />
            <IconItem text="Save all your favourite finds with just one tap of the heart icon" />
            <IconItem text="Share products easily on social media and WhatsApp" />
          </IconList>
        </div>
      </div>
    </div>

    <div css={styles.wrapper}>
      <div css={[styles.container, styles.narrowContainer]}>
        <div css={styles.imageWrapper}>
          <BannerImage
            imgUrl="https://images.contentstack.io/v3/assets/blt63f2f7afd19b09c2/blt979b2277e67c2660/69dfe1a300df56236898d433/Header_3.png"
            offset="bottom"
          />
        </div>
        <div css={[styles.content, styles.indentedContent]}>
          <IconList start={4}>
            <IconItem text="Enjoy speedy checkout with Apple Pay" />
            <IconItem text="Message sellers directly about your order" />
            <IconItem text="Be the first to find out about sales and discounts" />
          </IconList>
        </div>
      </div>
    </div>

    <div css={styles.wrapper}>
      <div css={[styles.container, styles.contentReverse, styles.narrowContainer]}>
        <div css={styles.imageWrapper}>
          <BannerImage
            imgUrl="https://images.contentstack.io/v3/assets/blt63f2f7afd19b09c2/bltecacd277f28595d3/69dfe1a300df5622ec98d437/Header_4.png"
            offset="bottom"
          />
        </div>
        <div css={[styles.content, styles.indentedContent]}>
          <IconList start={7}>
            <IconItem text="Use The Curator to help you find the perfect gifts, faster than ever." />
          </IconList>
        </div>
      </div>
    </div>

    <div css={styles.wrapper}>
      <div css={styles.qrCode}>
        <img alt="QR code to download the app" src={appQRCode} />
        <p>Scan me to download the app</p>
      </div>
    </div>
  </>
);

export default DownloadTheAppsPage;
